export type ISection = {
  title: string;
  forms: IForm[];
};

export type TFormType =
  | 'CHECKBOX'
  | 'COMPLEX'
  | 'DATE'
  | 'NUMBER'
  | 'MULTIPLE_CHOICE'
  | 'SELECT'
  | 'TEXTAREA'
  | 'TIME'
  | 'TEXT'
  | 'TITLE'
  | 'SUB_FORMS';

export type IForm<TChoices extends string[] = string[]> = {
  type: TFormType;
  title: string;
  id: string;
  noteOption?: {
    type?: TFormType;
    label: string;
    id: string;
  };
  isRequired?: boolean;
  hasOtherOption?: boolean;
  delimeter?: string;
  otherOptionLabel?: string;
  choices?: string[];
  requiredIf?: {
    key: IForm<TChoices>['id'];
    value: TChoices[number];
  };
  notRequiredIf?: {
    key: IForm<TChoices>['id'];
    value: TChoices[number];
  };
  subForms?: IForm[];
  placeholder?: string;
};

export const formSections = [
  {
    title: 'Initial Overview CKD',
    forms: [
      {
        type: 'MULTIPLE_CHOICE',
        title: 'How long have you been treated for CKD?',
        id: 'howLongTreatedCkd',
        choices: [
          '< 6 months',
          '1 year',
          '1-2 years',
          '3-5 years',
          '> 5 years',
          'Lifetime',
        ],
        noteOption: {
          type: 'TEXTAREA',
          label: 'How does CKD affect your life?',
          id: 'howLongTreatedCkdNote',
        },
      },
      {
        type: 'MULTIPLE_CHOICE',
        title:
          "How is Patient's understanding of the effects of CKD on the body?",
        id: 'effectsOnBody',
        choices: ['Good', 'Fair', 'Poor'],
      },
      {
        type: 'CHECKBOX',
        title: 'Which Provider do you work with to manage CKD?',
        id: 'manageCkdProvider',
        choices: ['PCP', 'Nephrologist'],
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Have you been hospitalized for CKD?',
        id: 'hospitalized',
        hasOtherOption: false,
        choices: ['Yes', 'No'],
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Have you received outpatient services for CKD?',
        id: 'outpatientServices',
        hasOtherOption: false,
        choices: ['Yes', 'No'],
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Do you have a family history of CKD?',
        id: 'familyHistory',
        hasOtherOption: false,
        choices: ['Yes', 'No'],
      },
      {
        type: 'TEXTAREA',
        title: 'Nurse Note:',
        id: 'nurseNote',
        hasOtherOption: false,
      },
    ],
  },
  {
    title: 'Signs & Symptoms',
    forms: [
      {
        type: 'CHECKBOX',
        title: 'Patient recognizes these Signs and Symptoms of CKD (Limit 4)',
        id: 'recognizesSignsSymptoms',
        choices: [
          'Swelling in hands, feet, or legs',
          'Discomfort or swelling to the abdomen',
          'Frequent urinary urgency',
          'Fatigue, lack of energy',
          'Loss of appetite',
          'Nausea, vomiting, taste changes',
          'Oral ulcerations',
        ],
      },
      {
        type: 'CHECKBOX',
        title:
          'SN instructed Patient on these Signs and Symptoms of CKD (Limit 4)',
        id: 'snInstructions',
        choices: [
          'Swelling in hands, feet, or legs',
          'Discomfort or swelling to the abdomen',
          'Frequent urinary urgency',
          'Fatigue, lack of energy',
          'Loss of appetite',
          'Nausea, vomiting, taste changes',
          'Oral ulcerations',
        ],
      },
      {
        type: 'CHECKBOX',
        title:
          'SN instructed Patient to notify Provider if any of these Signs and Symptoms occur',
        id: 'notifyProvider',
        choices: ['Yes'],
      },
      {
        type: 'CHECKBOX',
        title: 'SN utilized teach-back method with Patient',
        id: 'symptomsTeachBack',
        choices: ['Yes'],
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Was Patient able to verbalize understanding of the education?',
        id: 'symptomsVerbalizeEducation',
        choices: ['Able', 'Partially able', 'Unable'],
        noteOption: {
          type: 'TEXTAREA',
          label: 'Nurse Note:',
          id: 'symptomsVerbalizeEducationNote',
        },
      },
    ],
  },
  {
    title: 'Limiting Fluids and Monitors Weight',
    forms: [
      {
        type: 'CHECKBOX',
        title:
          'SN educated Patient on the importance of limiting fluids (Limit 4)',
        id: 'limitingFluids',
        choices: [
          'You need water to live, but when you have kidney disease, you may not need as much. This is because damaged kidneys do not get rid of extra fluid as well as they should',
          'Too much fluid in your body can be dangerous',
          'It can cause high blood pressure, swelling and heart failure',
          'Extra fluid can also build up around your lungs and make it hard to breathe',
          'Ask your Provider or dietitian if you need to limit fluids',
          "if you do need to limit fluids, measure your fluids and drink from small cups to help you keep track of how much you've had to drink",
          'Limit sodium to help cut down on thirst. At times, you may still feel thirsty',
        ],
      },
      {
        type: 'CHECKBOX',
        title: 'To help quench your thirst, you might try to (Limit 4)',
        id: 'helpQuenchThirst',
        choices: [
          'Avoid salty foods',
          'Stay cool on hot days',
          'Chew gum',
          'Rinse your mouth',
          'Suck on a piece of ice, mints or hard candy',
        ],
      },
      {
        type: 'CHECKBOX',
        title: 'SN utilized teach-back method with Patient',
        id: 'fluidsTeachBack',
        choices: ['Yes'],
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Was Patient able to verbalize understanding of the education?',
        id: 'fluidsVerbalizeEducation',
        choices: ['Able', 'Partially able', 'Unable'],
        noteOption: {
          type: 'TEXTAREA',
          label: 'Nurse Note:',
          id: 'fluidsVerbalizeEducationNote',
        },
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Do you monitor weight?',
        id: 'monitorWeight',
        choices: ['No', 'Yes, every day', 'Yes, but not every day'],
      },
      {
        type: 'CHECKBOX',
        title: 'SN instructed Patient on why monitoring weight is important',
        id: 'monitorWeightImportant',
        choices: [
          'A sudden weight gain can mean more fluid is building up in your body and your heart failure is getting worse',
          'Watch for weight gain of 2-3 pounds over 1-2 days or 5 pounds over a week',
          'You can gain up to 10 Ibs of "extra" weight before feeling bad or swelling',
          'Call your provider if you have a sudden weight gain',
        ],
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Do you have a scale?',
        id: 'haveScale',
        choices: [
          'Yes, Patient has a scale and uses it',
          'Yes, Patient has a scale but is not using it',
          'No',
        ],
      },
      {
        type: 'CHECKBOX',
        title: 'SN instructed Patient on consistent steps to measure weight',
        id: 'measureWeight',
        choices: [
          'Weigh at the same time in the morning, after you urinate',
          'Weigh while wearing what you sleep in, before eating or drinking',
          'Weigh using the same scale on a flat, hard surface',
          'After weighing, write your weight down on the weight log',
        ],
      },
      {
        type: 'COMPLEX',
        title: "What are Patient's recent weights?",
        id: 'recentWeights',
        subForms: [
          {
            id: 'vitalType',
            title: 'Select Vital Type',
            type: 'SELECT',
            choices: ['Weight'],
          },
          {
            id: 'vitalDate',
            title: 'Date',
            type: 'DATE',
          },
          {
            id: 'vitalTime',
            title: 'Time',
            type: 'TIME',
          },
          {
            id: 'vitalValue',
            title: 'Pounds',
            type: 'NUMBER',
            placeholder: 'ex. 150',
          },
        ],
      },
      {
        type: 'CHECKBOX',
        title: 'Daily Weight Monitoring',
        id: 'dailyWeightMonitoring',
        choices: [
          'A sudden weight gain can mean more fluid is building up in your body and your heart failure is getting worse',
          'Watch for weight gain of 2-3 pounds over 1-2 days or 5 pounds over a week',
          'You can gain up to 10 lbs of "extra" weight before feeling bad or swelling',
          'Call your provider if you have a sudden weight gain',
          'Weigh at the same time in the morning, after you urinate',
          'Weigh while wearing what you sleep in, before eating or drinking',
          'Weigh using the same scale on a flat, hard surface',
          'After weighing, write your weight down on the weight log',
        ],
      },
      {
        title: 'Diets-CKD',
        id: '_diets-ckd',
        type: 'TITLE',
      },
      {
        type: 'CHECKBOX',
        title: 'SN utilized teach-back method with Patient',
        id: 'weightTeachBack',
        choices: ['Yes'],
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Was Patient able to verbalize understanding of the education?',
        id: 'weightVerbalizeEducation',
        choices: ['Able', 'Partially able', 'Unable'],
        noteOption: {
          type: 'TEXTAREA',
          label: 'Nurse Note:',
          id: 'weightVerbalizeEducationNote',
        },
      },
      {
        type: 'TEXTAREA',
        title: 'Explain need for reinforcement',
        id: 'reinforcementNote',
      },
    ],
  },
  {
    title: 'Nutrition & Diet Counseling',
    forms: [
      {
        title: 'Low Salt (Sodium) Diet Counseling',
        id: '_low-sodium-diet-title',
        type: 'TITLE',
      },
      {
        type: 'CHECKBOX',
        title: 'SN educated Patient on Low Salt (Sodium) Diet - Education',
        id: 'lowSodiumEducation',
        choices: [
          'Typical daily sodium goal is less than 2,000 mg—check provider orders for individualized targets',
          'Read food labels carefully: verify serving size and amount of sodium per serving. Some products contain multiple servings',
          '"Low sodium" means less than 140 mg per serving and "sodium free" is less than 5 mg. "Reduced sodium" items may still be high—always check the numbers',
          'Common hidden sources of sodium: breads, rolls, deli/cured meats, cheese, processed foods, canned soups and vegetables, sauces (soy, BBQ), restaurant and fast food',
        ],
      },
      {
        type: 'CHECKBOX',
        title:
          'SN educated Patient on Low Salt (Sodium) Diet - Counseling Tips',
        id: 'lowSodiumCounselingTips',
        choices: [
          'Choose fresh foods whenever possible',
          'Use herbs, lemon, or vinegar for flavor instead of salt',
          'Rinse canned foods and vegetables to remove extra sodium',
          'Avoid adding salt at the table or during cooking',
          'Look for "no salt added" or "low sodium" versions of foods',
        ],
      },
      {
        title: 'Low Potassium (K) Diet Counseling',
        id: '_low-potassium-diet-title',
        type: 'TITLE',
      },
      {
        type: 'CHECKBOX',
        title: 'SN educated Patient on Low Potassium (K) Diet - Education',
        id: 'lowPotassiumEducation',
        choices: [
          'High-potassium foods to limit include: bananas, oranges, melons, apricots, kiwi, potatoes, tomatoes, leafy greens, pumpkin, squash, dairy, nuts/seeds, beans/lentils, avocados',
          'Lower-potassium food choices: apples, grapes, strawberries, blueberries, pineapple, watermelon, cucumbers, lettuce, cabbage, green beans, onions, peppers, white rice, white bread/pasta, eggs, chicken, lean meats',
          'Suggested substitutions: applesauce for bananas, green beans for spinach, apple or grape juice instead of orange juice',
        ],
      },
      {
        type: 'CHECKBOX',
        title:
          'SN educated Patient on Low Potassium (K) Diet - Counseling Tips',
        id: 'lowPotassiumCounselingTips',
        choices: [
          'Plan meals and snacks using lower-potassium foods',
          'When eating out or buying prepared foods, ask for ingredient information',
          'Use substitution examples to make safer choices without feeling deprived',
          "If you're unsure if a food is high or low in potassium, ask your nurse or dietitian",
        ],
      },
      {
        title: 'Low Phosphorus (PO4) Diet Counseling',
        id: '_low-phosphorus-diet-title',
        type: 'TITLE',
      },
      {
        type: 'CHECKBOX',
        title:
          'SN educated Patient on tips for limiting phosphorus in the diet for CKD',
        id: 'limitPhosphorusInDiet',
        choices: [
          'Dairy foods are the major source of phosphorus in the diet',
          'Limit milk to 1 cup per day',
          'If you use yogurt or cheese instead of liquid milk, have only one container of yogurt or 1.5 ounces of cheese per day',
          'White or Italian bread and low-salt crackers made with white flour have less phosphorus than whole-grain bread and crackers',
          'Soft drinks contain phosphorus, so only drink clear ones',
          'Beer also has phosphorus. Avoid all kinds',
        ],
      },
      {
        type: 'CHECKBOX',
        title:
          'SN educated Patient on Low Phosphorus (PO4) Diet - Counseling Tips',
        id: 'lowPhosphorusCounselingTips',
        choices: [
          'Choose fresh, unprocessed foods when possible',
          'Substitute high-phosphorus foods with lower-phosphorus alternatives like white bread or rice milk if approved',
          'Avoid dark colas and packaged snacks with "phos-" ingredients',
          'If you\'re not sure about a product, ask your care team or check the ingredient list for "phos-"',
        ],
      },
    ],
  },
  {
    title: 'Medication Reconciliation',
    forms: [
      {
        type: 'SUB_FORMS',
        title: 'SN educated Patient on medication reconciliation',
        id: '_medication-reconciliation-group',
        subForms: [
          {
            type: 'CHECKBOX',
            title: '',
            id: 'medicationReconciliationEducation',
            choices: [
              'Medication reconciliation completed',
              'Patient/caregiver understands the purpose, dose, and timing for each medication',
              'Any side effects or problems reported',
            ],
          },
          {
            type: 'MULTIPLE_CHOICE',
            title: 'Discrepancies',
            id: 'discrepancies',
            choices: ['Yes', 'No'],
          },
        ],
      },
      {
        type: 'CHECKBOX',
        title: 'Key Patient Counseling Tips',
        id: 'medicationReconciliationCounselingTips',
        choices: [
          'Always bring an up-to-date medication list or all medication bottles to each appointment',
          'Know what each medication is for and the correct time to take it',
          'Share any new or over-the-counter medications, supplements, or changes with your nurse or provider',
          'Report any side effects or issues with taking your medications right away',
        ],
      },
    ],
  },
] as const satisfies ISection[];

// If you have troubles with the type below
// and you can't fix more that 1 hour just delete it and use any type

type ExtractNoteIds<T extends readonly IForm[]> = {
  [K in keyof T]: T[K] extends { noteOption: { id: infer Id } }
    ? Id extends string
      ? Id
      : never
    : never;
}[number];

type GetFormValueType<F extends IForm> = F extends { type: 'TITLE' }
  ? never
  : F extends { type: 'SUB_FORMS' }
  ? never
  : F extends { type: 'CHECKBOX' }
  ? { value: string; label: string }[]
  : F extends { type: 'COMPLEX'; subForms: infer S }
  ? S extends readonly IForm[]
    ? { [P in S[number]['id']]: string }[]
    : string
  : string;

type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (
  k: infer I,
) => void
  ? I
  : never;

type ExtractSubFormIds<T extends readonly IForm[]> = UnionToIntersection<
  {
    [K in keyof T]: T[K] extends { subForms: infer S }
      ? S extends readonly IForm[]
        ? {
            [P in S[number]['id']]: GetFormValueType<
              Extract<S[number], { id: P }>
            >;
          }
        : {}
      : {};
  }[number]
>;

type ExtractStepValues<T extends readonly IForm[]> = {
  [K in T[number]['id']]: GetFormValueType<Extract<T[number], { id: K }>>;
} & {
  [K in ExtractNoteIds<T>]?: string;
} & ExtractSubFormIds<T>;

export type Step_1_Values = ExtractStepValues<
  (typeof formSections)[0]['forms']
>;
export type Step_2_Values = ExtractStepValues<
  (typeof formSections)[1]['forms']
>;
export type Step_3_Values = ExtractStepValues<
  (typeof formSections)[2]['forms']
>;
export type Step_4_Values = ExtractStepValues<
  (typeof formSections)[3]['forms']
>;
export type Step_5_Values = ExtractStepValues<
  (typeof formSections)[4]['forms']
>;
export type IValues = Partial<
  Step_1_Values & Step_2_Values & Step_3_Values & Step_4_Values & Step_5_Values
>;

export type SteppedValues = {
  STEP1: Partial<Step_1_Values>;
  STEP2: Partial<Step_2_Values>;
  STEP3: Partial<Step_3_Values>;
  STEP4: Partial<Step_4_Values>;
  STEP5: Partial<Step_5_Values>;
};
